import React from 'react';
import { Stack, Tooltip, Box, Unstable_Grid2 as Grid } from '@mui/material';

import { InfoField, Typography, InfoOutlinedIcon, Divider } from '@occc-app/design-system';
import { Hardware_Vendor } from '@occc/graphql';

import { loyaltyHeartbeatsOnlyEnabledTooltipMessage } from '../copies';
import gilbarco from '../components/LocationPluginFormContent/assets/gilbarco_mini_logo.png';
import verifone from '../components/LocationPluginFormContent/assets/verifone_mini_logo.png';
import ncr from '../components/LocationPluginFormContent/assets/ncr_mini_logo.png';
import oc from '../components/LocationPluginFormContent/assets/open_commerce_mini_logo.png';
import comdata from '../components/LocationPluginFormContent/assets/comdata_logo.png';
import mashgin from '../components/LocationPluginFormContent/assets/mashgin_mini_logo.png';

type LocationSiteInfoDetailsProps = {
  siteId: string;
  loyaltyStatusLabel: string;
  mobileStatusLabel: string;
  siteBrandTypeLabel: string;
  loyaltyHeartbeatsOnlyEnabled: boolean;
  payAtPumpEnabled: boolean;
  pluginConfiguration: Hardware_Vendor | null;
  mobileSiteSystem: Hardware_Vendor | null;
  loyaltyHostLabel: string;
};

const ICON_BY_VENDOR: { [key in Hardware_Vendor]: string } = {
  [Hardware_Vendor.Gilbarco]: gilbarco,
  [Hardware_Vendor.Ncr]: ncr,
  [Hardware_Vendor.NcrPes]: ncr,
  [Hardware_Vendor.Stuzo]: oc,
  [Hardware_Vendor.Verifone]: verifone,
  [Hardware_Vendor.Comdata]: comdata,
  [Hardware_Vendor.Mashgin]: mashgin,
};

const TITLE_BY_VENDOR: { [key in Hardware_Vendor]: string } = {
  [Hardware_Vendor.Gilbarco]: 'Gilbarco',
  [Hardware_Vendor.Ncr]: 'NCR-RLM',
  [Hardware_Vendor.NcrPes]: 'NCR-PES',
  [Hardware_Vendor.Stuzo]: 'Stuzo',
  [Hardware_Vendor.Verifone]: 'Verifone',
  [Hardware_Vendor.Comdata]: 'Comdata',
  [Hardware_Vendor.Mashgin]: 'Mashgin',
};

export const LocationSiteInfoDetails = ({
  siteId,
  loyaltyStatusLabel,
  mobileStatusLabel,
  loyaltyHeartbeatsOnlyEnabled,
  payAtPumpEnabled,
  pluginConfiguration,
  siteBrandTypeLabel,
  mobileSiteSystem,
  loyaltyHostLabel,
}: LocationSiteInfoDetailsProps) => {
  const icon = pluginConfiguration ? ICON_BY_VENDOR[pluginConfiguration] : '';
  const pluginConfigurationLabel = pluginConfiguration
    ? TITLE_BY_VENDOR[pluginConfiguration]
    : 'N/A';
  const mobileSiteSystemLabel = mobileSiteSystem ? TITLE_BY_VENDOR[mobileSiteSystem] : '';

  return (
    <>
      <Grid container spacing={2} pb={1}>
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Site ID' value={siteId} />
        </Grid>
        <Grid xs={12} sm={4} md={3}>
          <InfoField
            label='Plugin Configuration'
            disableTypography
            value={
              <Box display='flex' flexDirection='row' alignItems='center' gap={1}>
                {icon && <img src={icon} style={{ height: 15 }} alt='plugin icon' />}
                <Typography variant='body2'>{pluginConfigurationLabel}</Typography>
              </Box>
            }
          />
        </Grid>
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Site Brand Type' value={siteBrandTypeLabel} />
        </Grid>
      </Grid>
      <Divider />
      <Grid container spacing={2} pt={3}>
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Loyalty Enabled' value={loyaltyStatusLabel} />
        </Grid>
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Mobile Enabled' value={mobileStatusLabel} />
        </Grid>
        <Grid xs={12} sm={4} md={3}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <InfoField
              label='Loyalty Heartbeats Only Enabled'
              value={loyaltyHeartbeatsOnlyEnabled ? 'Enabled' : 'Disabled'}
            />
            <Tooltip title={loyaltyHeartbeatsOnlyEnabledTooltipMessage}>
              <InfoOutlinedIcon color='primary' />
            </Tooltip>
          </Stack>
        </Grid>
        {/* Line break to force next items on a new row */}
        <Grid md={3} display={{ xs: 'none', md: 'block' }} />
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Loyalty Host' value={loyaltyHostLabel} />
        </Grid>
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Mobile Site System' value={mobileSiteSystemLabel} />
        </Grid>
        <Grid xs={12} sm={4} md={3}>
          <InfoField label='Mobile Pay Enabled' value={payAtPumpEnabled ? 'Enabled' : 'Disabled'} />
        </Grid>
      </Grid>
    </>
  );
};
